import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Col, Form, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormInstance } from 'antd';
import { ScopeButton } from '../components/buttons';
import { FormPasswordInput } from '../components/formInputs';
import { LabelWrapper } from '../components/formWrappers';

type FormValues = {
  oldPassword?: string;
  newPassword: string;
  newPasswordConfirm: string;
};

type ChangePasswordFormProps = {
  hasPassword?: boolean;
  savePassword: (
    oldPassword: string,
    newPassword: string,
    newPasswordConfirm: string,
  ) => void;
  form: FormInstance;
};

export const ChangePasswordForm = ({
  savePassword,
  hasPassword,
  form,
}: ChangePasswordFormProps) => {
  const { t } = useTranslation();
  const passwordIconRender = useCallback(
    (visible: boolean) => (
      <button
        type="button"
        aria-label={
          visible ? t('common.hide-password') : t('common.show-password')
        }
        style={{
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: 0,
        }}
      >
        {visible ? (
          <EyeInvisibleOutlined aria-hidden />
        ) : (
          <EyeOutlined aria-hidden />
        )}
      </button>
    ),
    [t],
  );

  return (
    <Form
      name="basic"
      form={form}
      autoComplete="off"
      onFinish={(values: FormValues) =>
        savePassword(
          values.oldPassword || '',
          values.newPassword,
          values.newPasswordConfirm,
        )
      }
    >
      {hasPassword && (
        <>
          <LabelWrapper>
            <span className="label-text">{t('signup.old-password')}</span>
          </LabelWrapper>
          <Form.Item
            name="oldPassword"
            rules={[{ required: true, message: t('common.required-field') }]}
          >
            <FormPasswordInput
              aria-label={t('signup.old-password')}
              border_color="var(--color-border, #dbeae3)"
              iconRender={passwordIconRender}
            />
          </Form.Item>
        </>
      )}
      <Space style={{ columnGap: 15, marginTop: 10 }}>
        <Space direction="vertical">
          <LabelWrapper>
            <span className="label-text">{t('signup.new-password')}</span>
          </LabelWrapper>
          <Form.Item
            name="newPassword"
            rules={[
              { required: true, message: t('signup.pleaseEnterPassword') },
              { min: 8, message: '' },
            ]}
          >
            <FormPasswordInput
              aria-label={t('signup.new-password')}
              border_color="var(--color-border, #dbeae3)"
              iconRender={passwordIconRender}
            />
          </Form.Item>
        </Space>
        <Space direction="vertical">
          <LabelWrapper>
            <span className="label-text">{t('signup.repeatPassword')}</span>
          </LabelWrapper>
          <Form.Item
            name="newPasswordConfirm"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: t('signup.pleaseConfirmPassword') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('signup.passwordDoNotMatch')));
                },
              }),
            ]}
          >
            <FormPasswordInput
              aria-label={t('signup.repeatPassword')}
              border_color="var(--color-border, #dbeae3)"
              iconRender={passwordIconRender}
            />
          </Form.Item>
        </Space>
      </Space>
      <Col span={24}>
        <Form.Item>
          <ScopeButton width="172px" height="40px" type="primary" htmlType="submit">
            {hasPassword ? t('users.change-password') : t('users.add-password')}
          </ScopeButton>
        </Form.Item>
      </Col>
    </Form>
  );
};
